import React, { useMemo } from 'react';
import { FieldData, RecommendationOptions } from 'create/bulk/common/types';
import { useReviewPlanRows } from 'create/bulk/common/ScripGeneration/ReviewPlan/useReviewPlanRows';
import PlanTable from 'create/bulk/common/ScripGeneration/ReviewPlan/PlanTable';
import { RecommendationTableRow } from 'create/bulk/common/ScripGeneration/ReviewPlan/types';
import { getMessage } from 'util/locale';
import { ColumnDef } from '@tanstack/react-table';
import { formatAvgYield, formatNumberK } from 'util/formatters/numberFormatter';
import { allowDecimalByCountry } from 'user/selectors';
import { useAppSelector } from 'app/hooks';
import { getCurrency } from 'user/userSelectors';
import { DeltaValueCell } from 'create/bulk/common/ScripGeneration/CustomCells/DeltaValueCell';
import { inferUnit } from 'util/unitConversion';
import { Icon, FdsIcon, Tag, TagFill, ComponentSize, ComponentDensity, ComponentColor } from '@tcc/fds';
import { PreferredPlanItem } from './PreferredPlanItem';

const baseString = 'fields-list__hybrid';

export const HybridCard = ({ field } : { field: FieldData }) => {
  const isDecimalCountry = useAppSelector(allowDecimalByCountry);
  const currency = useAppSelector(getCurrency);

  const columns: ColumnDef<RecommendationTableRow>[] = useMemo(
    () => [
      {
        header: getMessage('field-level-seed-recs-rate-type'),
        cell: ({ row }) => {
          const rateType = row.original.id === RecommendationOptions.FVSS_RECOMMENDATION
            ? (
              <Tag
                testId="recommendation-label"
                density={ComponentDensity.Compact}
                size={ComponentSize.Sm}
                color={ComponentColor.Success}
                fill={TagFill.Soft}
              >
                {getMessage('text__recommended')}
              </Tag>
            ) : <span>{getMessage('field-level-seed-recs-rate-type-your-rate')}</span>;

          return (
            <span className="select-cell">
              {rateType}
              {row.original.isPreferredPlan && (
                <Tag
                  testId="preferred-plan"
                  density={ComponentDensity.Compact}
                  size={ComponentSize.Sm}
                  color={ComponentColor.Primary}
                  fill={TagFill.Soft}
                >
                  <Icon icon={FdsIcon.BookmarkStar} size={ComponentSize.Sm} />
                  {getMessage('field-level-seed-recs-preferred-rate')}
                </Tag>
              )}
            </span>
          );
        }
      },
      {
        header: getMessage(
          'bulk-fvss-review-scripts-avg-fixed-population-table-header',
          { unit: inferUnit('seed-density').symbol.quantitySpecific }
        ),
        cell: ({ row }) => {
          return (
            <label className="select-cell">
              <span>{formatNumberK(row.original.pop)}</span>
            </label>
          );
        },
      },
      {
        accessorKey: 'yield',
        header: `${getMessage('estimated-yield-short')} (${
          inferUnit('area-density.yield').symbol.quantitySpecific
        })`,
        cell: ({ row }) => (
          <span data-test-id={`yield-cell-${row.id}`}>
            {formatAvgYield(row.original.yield, isDecimalCountry)}
          </span>
        ),
      },
      {
        accessorKey: 'bags',
        header: getMessage('bags-needed'),
      },
      {
        accessorKey: 'seedCost',
        header:
            `${getMessage('price-product')} (${getMessage('per-unit', { unit: inferUnit('area').label.universal })})`,
      },
      {
        id: 'revenue',
        header: `${getMessage('bulk-fvss-review-plan-expected-revenue')} (${
          getMessage('per-unit', { unit: inferUnit('area').label.universal })
        })`,
        cell: ({ row }) => {
          return (
            <div className="revenue-cell">
              <span>{row.original.revenue}</span>
              {row.original.delta > 0 && (
                <DeltaValueCell
                  currency={currency}
                  delta={row.original.delta}
                />
              )}
            </div>
          );
        },
      },
    ],
    [isDecimalCountry, currency]
  );

  const data = useReviewPlanRows(field);

  const preferredPlan = useMemo(() => {
    return data.find(d => d.isPreferredPlan);
  }, [data]);

  return (
    <div className={baseString}>
      <header className={`${baseString}-header`}>
        <span className={`${baseString}-label`}>{getMessage('button-text__layer-selection--hybrid')}:</span>
        <span className={`${baseString}-name`}>{field.hybrid.name}</span>
        <Tag fill={TagFill.Outlined} size={ComponentSize.Sm} density={ComponentDensity.Compact}>
          <Icon icon={FdsIcon.Corn} size={ComponentSize.Sm} />
          {getMessage('corn')}
        </Tag>
      </header>
      <section className={`${baseString}-details`}>
        <PreferredPlanItem
          label={getMessage(
            'bulk-fvss-review-scripts-avg-fixed-population-table-header',
            { unit: inferUnit('seed-density').symbol.quantitySpecific }
          )}
          value={formatAvgYield(preferredPlan.pop, isDecimalCountry)}
        />
        <PreferredPlanItem
          label={getMessage('bulk-fvss-review-scripts-seed-cost-per-bag-table-header')}
          value={preferredPlan.seedCost}
        />
        <PreferredPlanItem
          label={`${getMessage('target-yield')} (${inferUnit('area-density.yield').symbol.quantitySpecific})`}
          value={formatAvgYield(preferredPlan.yield, isDecimalCountry)}
        />
      </section>
      <section className="review-plan-section">
        <PlanTable data={data} columns={columns} />
      </section>
    </div>
  );
};
